import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/ui/Sidebar';
import { AuxWithSchedules } from '@/types/dashboard';
import { SPA_ELECTRICS_COLORS, AQUAQUIP_COLORS, PAL_COLORS } from '@/utils/utils';
import { AuxTypeCode, LightTypeMode } from '@/types/device';

interface LightControlSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  device: AuxWithSchedules | null;
  onColorSelect: (device: AuxWithSchedules,colorValue: number) => void;
}

const LightControlSidebar: React.FC<LightControlSidebarProps> = ({
  isOpen,
  onClose,
  device,
  onColorSelect,
}) => {
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  
  // 初始化selectedColor为设备的colorMode
  useEffect(() => {
    if (device) {
      // 优先使用colorMode，如果没有则使用color
      setSelectedColor(device.lightColorMode || null);
    }
  }, [device]);

  if (!isOpen || !device) return null;

  const effectiveLightTypeMode =
    (device.aux.aux_type_code === AuxTypeCode.PAL_LIGHTING ||
      device.aux.aux_type_code === AuxTypeCode.SPA_PAL_LIGHTING) &&
    (device.lightTypeMode == null || device.lightTypeMode === LightTypeMode.LIGHT_UNKNOWN)
      ? LightTypeMode.PAL_COLOUR_TOUCH
      : device.lightTypeMode;

  const sidebarTitle = (() => {
    switch (effectiveLightTypeMode) {
      case LightTypeMode.AQUAQUIP_INSTA_TOUCH:
        return 'Aqua Equip - Insta Touch';
      case LightTypeMode.AQUAQUIP_MULTI_COLOUR:
        return 'Aqua Equip - Multi Colour';
      case LightTypeMode.SPA_ELECTRICS_MULTI_PLUS:
        return 'SPA Electrics - Multi Plus';
      case LightTypeMode.SPA_ELECTRICS_MULTI_COLOUR:
        return 'SPA Electrics - Multi Colour';
      case LightTypeMode.PAL_COLOUR_TOUCH:
        return 'PAL Lighting - Colour Touch';
      default:
        return 'Colour';
    }
  })();

  const handleColorSelect = async (colorValue: number) => {
    try {
      console.info('colorValue', colorValue);
      if (device) {
        onColorSelect(device, colorValue);
      }
    } catch (error) {
      console.error('Failed to set light color:', error);
    }
  };

  const handleNextColor = () => {
    handleColorSelect(2); // TWO value for next color
  };

  const handleSaveColor = () => {
    if (selectedColor !== null && device) {
      onColorSelect(device, selectedColor);
    }
  };

  const handleSyncColor = () => {
    handleColorSelect(12); // TWELVE value for sync
  };

  const handleReset = () => {
    handleColorSelect(0); // RESET value
  };

  const renderColorGrid = (modes: typeof AQUAQUIP_COLORS | typeof SPA_ELECTRICS_COLORS | typeof PAL_COLORS) => {
    return (
      <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
        {modes.map((mode) => (
          <div 
            key={mode.value}
            onClick={() => {
              console.log(`Selected color: ${mode.name} with value: ${mode.value}`);
              setSelectedColor(mode.value);
              if (device) {
                onColorSelect(device, mode.value);
              }
            }}
            className="flex items-center justify-between py-4 px-2 cursor-pointer border-b border-gray-200 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800/80"
          >
            <div className="flex items-center space-x-4">
              <div
                className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0"
                style={{
                  background: mode.colors.length > 1 
                    ? `linear-gradient(135deg, ${mode.colors.join(', ')})`
                    : mode.colors[0]
                }}
              />
              <div className="text-base font-medium text-gray-800 dark:text-gray-100">{mode.name}</div>
            </div>
            {selectedColor === mode.value && (
              <div className="text-green-500 dark:text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderInstaTouchControls = () => {
    return (
      <div className="space-y-4">
        <button
          onClick={handleNextColor}
          className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Next Colour
        </button>
        <button
          onClick={handleReset}
          className="w-full py-3 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Reset
        </button>
      </div>
    );
  };

  const renderMultiPlusControls = () => {
    return (
      <div className="space-y-4">
        <button
          onClick={handleNextColor}
          className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Next Colour
        </button>
        <button
          onClick={handleSyncColor}
          className="w-full py-3 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          Sync Colour
        </button>
        <button
          onClick={handleReset}
          className="w-full py-3 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Reset
        </button>
      </div>
    );
  };

  const renderContent = () => {
    console.log('renderContent - device.lightTypeMode:', device, device.lightTypeMode);
    switch (effectiveLightTypeMode) {
      case LightTypeMode.AQUAQUIP_INSTA_TOUCH:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">AquaQuip Insta Touch</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Select a color mode for your light</p>
            </div>
            {renderColorGrid(AQUAQUIP_COLORS)}
          </div>
        );

      case LightTypeMode.PAL_COLOUR_TOUCH:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">PAL Lighting - Colour Touch</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Select a color mode for your light</p>
            </div>
            {renderColorGrid(PAL_COLORS)}
          </div>
        );
      
      case LightTypeMode.AQUAQUIP_MULTI_COLOUR:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">AquaQuip Multi Colour</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Use the buttons below to control your light</p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => {
                  // AquaQuip Multi Colour - Next -> AquaQuipMode.ONE (value: 1)
                  handleColorSelect(1);
                }}
                className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Next Colour
              </button>
              <button
                onClick={() => {
                  // AquaQuip Multi Colour - Save -> AquaQuipMode.TWO (value: 2)
                  handleColorSelect(2);
                }}
                className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Save Colour
              </button>
              <button
                onClick={() => {
                  // AquaQuip Multi Colour - Sync -> AquaQuipMode.RESET (value: 0)
                  handleColorSelect(0);
                }}
                className="w-full py-3 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Sync Colour
              </button>
            </div>
          </div>
        );
      
      case LightTypeMode.SPA_ELECTRICS_MULTI_PLUS:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Spa Electrics Multi Plus</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Select a color mode for your light</p>
            </div>
            {renderColorGrid(SPA_ELECTRICS_COLORS)}
          </div>
        );
      
      case LightTypeMode.SPA_ELECTRICS_MULTI_COLOUR:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Spa Electrics Multi Colour</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Use the buttons below to control your light</p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => {
                  // Spa Electrics Multi Colour - Next -> SpaElectricsMode.ONE (value: 1)
                  handleColorSelect(1);
                }}
                className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Next Colour
              </button>
              <button
                onClick={() => {
                  // Spa Electrics Multi Colour - Sync -> SpaElectricsMode.RESET (value: 0)
                  handleColorSelect(0);
                }}
                className="w-full py-3 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Sync Colour
              </button>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-300">Unsupported light type: {effectiveLightTypeMode}</p>
          </div>
        );
    }
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      title={sidebarTitle}
    >
      <div className="p-4 text-gray-900 dark:text-gray-100">
        {renderContent()}
      </div>
    </Sidebar>
  );
};

export default LightControlSidebar;
